import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { STATS } from "../../content/catalog";

function Stat({ value, label }) {
  const ref = useRef(null);
  const suffix = value > 9 ? "+" : "";

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const finalText = `${value}${suffix}`;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      el.textContent = finalText;
      return undefined;
    }
    let n = 0;
    const timer = window.setInterval(() => {
      n += 1;
      el.textContent = `${n}${suffix}`;
      if (n >= value) window.clearInterval(timer);
    }, 1400 / value);
    return () => window.clearInterval(timer);
  }, [value, suffix]);

  return (
    <div>
      <b ref={ref} aria-hidden="true">0</b>
      <span className="sr-only">{value}{suffix}</span>
      <span>{label}</span>
    </div>
  );
}

function Wireframe() {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d", { alpha: false });
    const state = { W: 0, H: 0, mx: 0.5, my: 0.5, t: 0, running: true, raf: 0 };
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let alive = true;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const width = canvas.offsetWidth || 1;
      const height = canvas.offsetHeight || 1;
      canvas.width = Math.max(1, Math.floor(width * dpr));
      canvas.height = Math.max(1, Math.floor(height * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      state.W = width;
      state.H = height;
    };

    const frame = () => {
      const { W, H, mx, my } = state;
      if (!reduced) state.t += 0.008;
      const t = state.t;
      const light = document.documentElement.getAttribute("data-theme") === "light";
      ctx.fillStyle = light ? "#f7f5fc" : "#07060b";
      ctx.fillRect(0, 0, W, H);
      const cx = W * (0.68 + (mx - 0.5) * 0.08);
      const cy = H * (0.5 + (my - 0.5) * 0.08);
      const R = Math.min(W, H) * 0.36;
      for (let i = 0; i < 46; i += 1) {
        const a = (i / 46) * Math.PI;
        ctx.beginPath();
        for (let j = 0; j <= 80; j += 1) {
          const b = (j / 80) * Math.PI * 2;
          let px = Math.sin(a) * Math.cos(b);
          let py = Math.cos(a);
          let pz = Math.sin(a) * Math.sin(b);
          const warp = 1 + 0.18 * Math.sin(3 * b + t * 3 + i * 0.3) * Math.cos(2 * a + t * 2);
          px *= warp;
          py *= warp;
          pz *= warp;
          const ry = t + mx;
          const rx = 0.4 + my * 0.5;
          const X = px * Math.cos(ry) - pz * Math.sin(ry);
          let Z = px * Math.sin(ry) + pz * Math.cos(ry);
          const Y = py * Math.cos(rx) - Z * Math.sin(rx);
          Z = py * Math.sin(rx) + Z * Math.cos(rx);
          const s = R / (1.8 - Z * 0.5);
          if (j) ctx.lineTo(cx + X * s, cy + Y * s);
          else ctx.moveTo(cx + X * s, cy + Y * s);
        }
        const hue = (i * 6 + t * 60) % 360;
        ctx.strokeStyle = light
          ? `hsla(${80 + hue * 0.8},90%,34%,.5)`
          : `hsla(${80 + hue * 0.8},100%,60%,.55)`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }
    };

    const minDelta = window.innerWidth < 820 ? 32 : 16;
    let last = 0;
    const loop = (now) => {
      if (state.running && !document.hidden && now - last >= minDelta) {
        last = now;
        frame();
      }
      state.raf = window.requestAnimationFrame(loop);
    };

    const onMove = (event) => {
      state.mx = event.clientX / window.innerWidth;
      state.my = event.clientY / window.innerHeight;
    };

    resize();
    frame();
    if (!reduced) {
      const start = () => {
        if (alive) state.raf = window.requestAnimationFrame(loop);
      };
      if (window.requestIdleCallback) window.requestIdleCallback(start, { timeout: 500 });
      else window.setTimeout(start, 250);
      window.addEventListener("mousemove", onMove, { passive: true });
    }
    window.addEventListener("resize", resize);
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    const io = new IntersectionObserver(([entry]) => {
      state.running = entry.isIntersecting;
    });
    io.observe(canvas);
    const themeObserver = new MutationObserver(() => frame());
    themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });

    return () => {
      alive = false;
      themeObserver.disconnect();
      window.cancelAnimationFrame(state.raf);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("resize", resize);
      ro.disconnect();
      io.disconnect();
    };
  }, []);

  return <canvas ref={ref} className="hero-canvas" aria-hidden="true" />;
}

export default function Hero() {
  const { t } = useTranslation();
  const glitch = t("hero.glitch");
  const labels = t("hero.stats", { returnObjects: true });

  return (
    <section className="hero" id="hero">
      <Wireframe />
      <div className="hero-foreground">
        <span className="tag mono"><i /> {t("hero.tag")}</span>
        <h1 aria-label={t("hero.aria")}>
          <span className="o">{t("hero.w1")}</span>
          <br />
          <span className="glitch" data-t={glitch}>{glitch}</span>
          {t("hero.join")}
          <span className="o">{t("hero.w2")}</span>
          <br />
          <span className="g">{t("hero.w3")}</span>
        </h1>
        <p className="sub">{t("hero.sub")}</p>
        <div className="btns">
          <a className="btn p" href="#iletisim">{t("hero.cta1")} ↗</a>
          <a className="btn s" href="#referanslar">{t("hero.cta2")}</a>
        </div>
        <div className="stats">
          {STATS.map((value, index) => (
            <Stat key={labels[index] || index} value={value} label={labels[index]} />
          ))}
        </div>
      </div>
    </section>
  );
}
